import React from 'react';

// Simple markdown parser for basic formatting
export function MarkdownRenderer({ content }) {
  const parseMarkdown = (text) => {
    if (!text) return '';
    
    // Split into lines for better processing
    const lines = text.split('\n');
    let html = '';
    let inList = false;
    let listLevel = 0;
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      
      // Check for headers
      if (line.match(/^### /)) {
        if (inList) { html += '</ul>'; inList = false; }
        html += line.replace(/^### (.*$)/, '<h3 class="text-lg font-semibold text-white mb-2 mt-4">$1</h3>');
      } else if (line.match(/^## /)) {
        if (inList) { html += '</ul>'; inList = false; }
        html += line.replace(/^## (.*$)/, '<h2 class="text-xl font-bold text-white mb-3 mt-6">$1</h2>');
      } else if (line.match(/^# /)) {
        if (inList) { html += '</ul>'; inList = false; }
        html += line.replace(/^# (.*$)/, '<h1 class="text-2xl font-bold text-white mb-4 mt-0">$1</h1>');
      }
      // Check for lists (with indentation support)
      else if (line.match(/^(\s*)-\s+/)) {
        const indent = (line.match(/^(\s*)/) || ['', ''])[1].length;
        const content = line.replace(/^\s*-\s+/, '');
        
        if (!inList) {
          html += '<ul class="list-none space-y-1 mb-4">';
          inList = true;
          listLevel = indent;
        }
        
        // Apply indentation based on spaces
        const indentClass = indent > 0 ? `ml-${Math.min(indent, 8)}` : '';
        html += `<li class="text-white/80 ${indentClass}"><span class="text-cyan-400 mr-2">•</span>${formatInlineMarkdown(content)}</li>`;
      }
      // Check for numbered lists
      else if (line.match(/^\d+\.\s+/)) {
        if (inList) { html += '</ul>'; inList = false; }
        const content = line.replace(/^\d+\.\s+/, '');
        html += `<div class="text-white/80 ml-4 mb-1"><span class="text-cyan-400 mr-2">•</span>${formatInlineMarkdown(content)}</div>`;
      }
      // Empty line - close lists and add paragraph break
      else if (line.trim() === '') {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        // Only add paragraph break if next line has content
        if (i < lines.length - 1 && lines[i + 1].trim() !== '') {
          html += '<div class="mb-3"></div>';
        }
      }
      // Regular text
      else if (line.trim() !== '') {
        if (inList) { html += '</ul>'; inList = false; }
        html += `<p class="text-white/80 mb-3">${formatInlineMarkdown(line)}</p>`;
      }
    }
    
    // Close any remaining lists
    if (inList) {
      html += '</ul>';
    }
    
    return html;
  };
  
  const formatInlineMarkdown = (text) => {
    return text
      // Links (must be processed before bold/italic to avoid conflicts)
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-400 hover:text-cyan-300 underline">$1</a>')
      // Bold and Italic
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic text-white/90">$1</em>')
      // Code
      .replace(/`(.*?)`/g, '<code class="bg-gray-800 text-cyan-400 px-1 py-0.5 rounded text-sm font-mono">$1</code>');
  };

  return (
    <div 
      className="prose prose-invert max-w-none"
      dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
    />
  );
}
